import { BrowserRouter, Route, Routes } from "react-router-dom";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import { ToastContainer } from "react-toastify";
import PrivateRoute from "./PrivateRoute";
import { AuthProvider } from "./context/AuthContext";

/* IMPORT ALL YOUR PAGES HERE */
import HomePageOne from "./pages/HomePageOne";

import ErrorPage from "./pages/ErrorPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";

import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";

import ViewProfilePage from "./pages/ViewProfilePage";

import BlogPage from "./pages/BlogPage";
import BlogDetailsPage from "./pages/BlogDetailsPage";
import AddBlogPage from "./pages/AddBlogPage";
import { OutletsList } from "./pages/OutletsList";
import { AddOutlet } from "./pages/AddOutlet";
import { AddCategory } from "./pages/AddCategory";
import { CategoryListPage } from "./pages/CategoryListPage";
import { AddUnitPage } from "./pages/AddUnitPage";
import { UnitListPage } from "./pages/UnitListPage";
import { AddSupplierPage } from "./pages/AddSupplierPage";
import { SuppliersListPage } from "./pages/SupplierListPage";
import { WoodTypeListPage } from "./pages/WoodTypeListPage";
import { AddWoodTypePage } from "./pages/AddWoodType";
import { SubCategoryListPage } from "./pages/SubCategoryListPage";
import { AddSubCategoryPage } from "./pages/AddSubCategoryPage";
import { CalculateCFTPage } from "./pages/CalculateCFTPage";
import RawMaterialListPage from "./pages/RawMaterialListPage";
import AddRawMaterialPage from "./pages/AddRawMaterialPage";
import { NonInventoryItemListPage } from "./pages/NonInventoryItemListPage";
import { AddNonInventoryItemPage } from "./pages/AddNonInventoryItemPage";
import { AddPurchaseOrderPage } from "./pages/AddPurchaseOrderPage";
import { PurchaseOrderListPage } from "./pages/PurchaseOrderListPage";
import { AddRatesPage } from "./pages/AddRatesPage";
import { RatesListPage } from "./pages/RatesListPage";
import { AddLeaguePage } from "./pages/AddLeaguePage";
import { LeagueListPage } from "./pages/LeagueListPage";
import { AddMatchPage } from "./pages/AddMatchPage";
import { MatchListPage } from "./pages/MatchListPage";
import { AddQuestiongamePage } from "./pages/AddQuestiongamePage";
import { QuestiongameListPage } from "./pages/QuestiongameListPage";
import { AddGameResultPage } from "./pages/AddGameResultPage";
import { GameResultListPage } from "./pages/GameResultListPage";
import { AddGamesPoints } from "./pages/AddGamesPoints";
import { GamesPointsListPage } from "./pages/GamesPointsListPage";
import AddUserPage from "./pages/AddUserPage";
import UsersListPage from "./pages/UsersListPage";
import { AddPackagePage } from "./pages/AddPackagePage";
import { PackageListPage } from "./pages/PackageListPage";
import { UserPointPageList } from "./pages/UserPointPageList";
import { TeamListPage } from "./pages/TeamListPage";
import { AddTeamPage } from "./pages/AddTeamPage";
import { WithdrawalsListPage } from "./pages/WithdrawalsListPage";
import { BonsusPage } from "./pages/BonsusPage";
import { MatchresultPage } from "./pages/MatchresultPage";
import { PointsGamesResultPage } from "./pages/PointsGamesResultPage";
import { FeaturedMediaLayer } from "./components/featuredMedia/FeaturedMediaLayer";
import { FeaturedMediaPage } from "./pages/FeaturedMediaPage";
import { FeaturedMediaList } from "./pages/FeaturedMediaList";
import { DemoUserListPage } from "./pages/DemoUserListPage";
import { AddAsectionsPage } from "./pages/AddAsectionsPage";
import { AsectionsListPage } from "./pages/AsectionsListPage";
import { AddAsectionsQuestionsResultPage } from "./pages/AddAsectionsQuestionsResultPage";
import { AsectionsQuestionsResultListPage } from "./pages/AsectionsQuestionsResultListPage";
import { AddBPointsGamesResultPage } from "./pages/AddBPointsGamesResultPage";
import { BPointsGamesResulListPage } from "./pages/BPointsGamesResulListPage";
import { AddAllSectionPage } from "./pages/AddAllSectionPage";
import { SectionAListPage } from "./pages/SectionAListPage";
import { SectionBListPage } from "./pages/SectionBListPage";
import { SectionCListPage } from "./pages/SectionCListPage";
import { AllReportPageList } from "./pages/AllReportPageList";
import { SectionAReportPageList } from "./pages/SectionAReportPageList";
import { SectionBReportPageList } from "./pages/SectionBReportPageList";
import { SectionCReportPageList } from "./pages/SectionCReportPageList";
import { AllUserReportPageList } from "./pages/AllUserReportPageList";
import { MatchBetsReportPage } from "./pages/MatchBetsReportPage";
import { UserBetsReportPage } from "./pages/UserBetsReportPage";
import { User360ReportPage } from "./pages/User360ReportPage";
import { UpiMangePage } from "./pages/UpiMangePage";
import { PaymentListPage } from "./pages/PaymentListPage";
import { AdsListPage } from "./pages/AdsListPage";
import { AddAdsPage } from "./pages/AddAdsPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import RoleManagementPage from "./pages/RoleManagementPage";

// Tiny helper to cut down repetition in the Routes block.
const Guarded = ({ permission, anyOf, children }) => (
  <PrivateRoute permission={permission} anyOf={anyOf}>
    {children}
  </PrivateRoute>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteScrollToTop />
        <ToastContainer />

        <Routes>
          {/* PUBLIC ROUTES */}
          <Route exact path="/sign-in" element={<SignInPage />} />
          <Route exact path="/sign-up" element={<SignUpPage />} />
          <Route exact path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* PROTECTED ROUTES */}
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Routes>
                  {/* Always-available for any signed-in admin user */}
                  <Route exact path="/" element={<Guarded permission="dashboard.view"><HomePageOne /></Guarded>} />
                  <Route exact path="/view-profile" element={<ViewProfilePage />} />
                  <Route exact path="/access-denied" element={<AccessDeniedPage />} />

                  {/* ── Admin / Roles ── */}
                  <Route path="/admin-users" element={<Guarded permission="admin_users.view"><AdminUsersPage /></Guarded>} />
                  <Route path="/role-management" element={<Guarded permission="roles.view"><RoleManagementPage /></Guarded>} />

                  {/* ── Sections overview ── */}
                  <Route exact path="/Add-all-section" element={<Guarded permission="sections.add_all"><AddAllSectionPage /></Guarded>} />
                  <Route exact path="/section-a-list" element={<Guarded permission="section_a.list"><SectionAListPage /></Guarded>} />
                  <Route exact path="/section-b-list" element={<Guarded permission="section_b.list"><SectionBListPage /></Guarded>} />
                  <Route exact path="/section-c-list" element={<Guarded permission="section_c.list"><SectionCListPage /></Guarded>} />

                  {/* ── Game setup ── */}
                  <Route exact path="/add-league" element={<Guarded permission="leagues.manage"><AddLeaguePage /></Guarded>} />
                  <Route exact path="/league-list" element={<Guarded permission="leagues.manage"><LeagueListPage /></Guarded>} />
                  <Route exact path="/add-match" element={<Guarded permission="matches.manage"><AddMatchPage /></Guarded>} />
                  <Route path="/add-match/:id" element={<Guarded permission="matches.manage"><AddMatchPage /></Guarded>} />

                  <Route path="/match-result" element={<Guarded permission="section_a.match_result"><MatchresultPage /></Guarded>} />
                  <Route path="/match-result/:id" element={<Guarded permission="section_a.match_result"><MatchresultPage /></Guarded>} />

                  {/* ── Reports ── */}
                  <Route path="/all-reports" element={<Guarded permission="reports.all"><AllReportPageList /></Guarded>} />
                  <Route path="/all-user-reports" element={<Guarded permission="reports.all_user"><AllUserReportPageList /></Guarded>} />
                  <Route path="/section-a-reports" element={<Guarded permission="reports.section_a"><SectionAReportPageList /></Guarded>} />
                  <Route path="/section-b-reports" element={<Guarded permission="reports.section_b"><SectionBReportPageList /></Guarded>} />
                  <Route path="/section-c-reports" element={<Guarded permission="reports.section_c"><SectionCReportPageList /></Guarded>} />
                  <Route path="/match-bets-report" element={<Guarded permission="reports.match_bets"><MatchBetsReportPage /></Guarded>} />
                  <Route path="/user-bets-report" element={<Guarded permission="reports.user_bets"><UserBetsReportPage /></Guarded>} />
                  <Route path="/user-360-report" element={<Guarded permission="user_360.view"><User360ReportPage /></Guarded>} />

                  {/* ── Section A ── */}
                  <Route path="/add-a-sections" element={<Guarded permission="section_a.questions"><AddAsectionsPage /></Guarded>} />
                  <Route path="/add-a-sections/:id" element={<Guarded permission="section_a.questions"><AddAsectionsPage /></Guarded>} />
                  <Route path="/asections-list" element={<Guarded permission="section_a.questions"><AsectionsListPage /></Guarded>} />
                  <Route path="/asections-questions-result" element={<Guarded permission="section_a.result_add"><AddAsectionsQuestionsResultPage /></Guarded>} />
                  <Route path="/asections-questions-result-list" element={<Guarded permission="section_a.result_list"><AsectionsQuestionsResultListPage /></Guarded>} />

                  {/* ── Packages ── */}
                  <Route exact path="/add-package" element={<Guarded permission="packages.manage"><AddPackagePage /></Guarded>} />
                  <Route exact path="/add-package/:id" element={<Guarded permission="packages.manage"><AddPackagePage /></Guarded>} />
                  <Route exact path="/package-list" element={<Guarded permission="packages.view"><PackageListPage /></Guarded>} />

                  {/* ── Teams ── */}
                  <Route exact path="/add-team" element={<Guarded permission="teams.manage"><AddTeamPage /></Guarded>} />
                  <Route exact path="/add-team/:id" element={<Guarded permission="teams.manage"><AddTeamPage /></Guarded>} />
                  <Route exact path="/team-list" element={<Guarded permission="teams.manage"><TeamListPage /></Guarded>} />

                  {/* ── Finance ── */}
                  <Route exact path="/user-point-list" element={<Guarded permission="user_points.view"><UserPointPageList /></Guarded>} />
                  <Route exact path="/withdrawals-list" element={<Guarded permission="withdrawals.view"><WithdrawalsListPage /></Guarded>} />
                  <Route exact path="/bonus-management" element={<Guarded permission="bonus.view"><BonsusPage /></Guarded>} />
                  <Route path="/upi-manage" element={<Guarded permission="upi.view"><UpiMangePage /></Guarded>} />
                  <Route path="/payment-list" element={<Guarded permission="payments.view"><PaymentListPage /></Guarded>} />

                  {/* ── Ads / Banners ── */}
                  <Route path="/ads-list" element={<Guarded permission="ads.view"><AdsListPage /></Guarded>} />
                  <Route path="/add-ad" element={<Guarded permission="ads.manage"><AddAdsPage /></Guarded>} />
                  <Route path="/add-ad/:id" element={<Guarded permission="ads.manage"><AddAdsPage /></Guarded>} />

                  {/* ── Notifications ── */}
                  <Route path="/notifications" element={<Guarded permission="notifications.view"><NotificationsPage /></Guarded>} />

                  {/* ── Section C ── */}
                  <Route exact path="/add-questiongame" element={<Guarded permission="section_c.question_add"><AddQuestiongamePage /></Guarded>} />
                  <Route path="/add-questiongame/:id" element={<Guarded permission="section_c.question_add"><AddQuestiongamePage /></Guarded>} />
                  <Route path="/questiongame-list" element={<Guarded permission="section_c.question_list"><QuestiongameListPage /></Guarded>} />
                  <Route exact path="/add-gameresult" element={<Guarded permission="section_c.result_add"><AddGameResultPage /></Guarded>} />
                  <Route path="/add-gameresult/:id" element={<Guarded permission="section_c.result_add"><AddGameResultPage /></Guarded>} />
                  <Route exact path="/gameresult-list" element={<Guarded permission="section_c.result_list"><GameResultListPage /></Guarded>} />

                  {/* ── Featured Media (kept, generic) ── */}
                  <Route exact path="/featured-media" element={<FeaturedMediaPage />} />
                  <Route exact path="/featured-media-list" element={<FeaturedMediaList />} />

                  {/* ── Match list ── */}
                  <Route exact path="/match-list" element={<Guarded permission="matches.manage"><MatchListPage /></Guarded>} />

                  {/* ── App users ── */}
                  <Route exact path="/add-user" element={<Guarded permission="users.create"><AddUserPage /></Guarded>} />
                  <Route exact path="/add-user/:id" element={<Guarded permission="users.edit"><AddUserPage /></Guarded>} />
                  <Route exact path="/users-list" element={<Guarded permission="users.view"><UsersListPage /></Guarded>} />
                  <Route exact path="/demo-user-list" element={<Guarded permission="demo_users.view"><DemoUserListPage /></Guarded>} />

                  {/* ── Section B ── */}
                  <Route exact path="/add-games-points" element={<Guarded permission="section_b.points_add"><AddGamesPoints /></Guarded>} />
                  <Route exact path="/games-points-list" element={<Guarded permission="section_b.points_list"><GamesPointsListPage /></Guarded>} />
                  <Route exact path="/add-points-result" element={<Guarded permission="section_b.result_add"><AddBPointsGamesResultPage /></Guarded>} />
                  <Route exact path="/points-result-list" element={<Guarded permission="section_b.result_list"><BPointsGamesResulListPage /></Guarded>} />

                  {/* ── Legacy / unused pages (kept accessible to signed-in) ── */}
                  <Route exact path="/add-outlet" element={<AddOutlet />} />
                  <Route path="/add-outlet/:id" element={<AddOutlet />} />
                  <Route exact path="/blog" element={<BlogPage />} />
                  <Route exact path="/blog-details" element={<BlogDetailsPage />} />
                  <Route exact path="/add-blog" element={<AddBlogPage />} />
                  <Route exact path="/outlets-list" element={<OutletsList />} />
                  <Route exact path="/add-category" element={<AddCategory />} />
                  <Route path="/edit-category/:id" element={<AddCategory />} />
                  <Route exact path="/category-list" element={<CategoryListPage />} />
                  <Route exact path="/sub-category" element={<AddSubCategoryPage />} />
                  <Route path="/edit-sub-category/:id" element={<AddSubCategoryPage />} />
                  <Route exact path="/sub-category-list" element={<SubCategoryListPage />} />
                  <Route exact path="/add-unit" element={<AddUnitPage />} />
                  <Route path="/edit-unit/:id" element={<AddUnitPage />} />
                  <Route exact path="/unit-list" element={<UnitListPage />} />
                  <Route path="/add-supplier" element={<AddSupplierPage />} />
                  <Route path="/edit-supplier/:id" element={<AddSupplierPage />} />
                  <Route path="/supplier-list" element={<SuppliersListPage />} />
                  <Route path="/add-wood-type" element={<AddWoodTypePage />} />
                  <Route path="/edit-wood-type/:id" element={<AddWoodTypePage />} />
                  <Route path="/wood-type-list" element={<WoodTypeListPage />} />
                  <Route path="/calculate-cft" element={<CalculateCFTPage />} />
                  <Route path="/add-raw-material" element={<AddRawMaterialPage />} />
                  <Route path="/edit-raw-material/:id" element={<AddRawMaterialPage />} />
                  <Route path="/raw-materials" element={<RawMaterialListPage />} />
                  <Route path="/add-non-inventory-item" element={<AddNonInventoryItemPage />} />
                  <Route path="/edit-non-inventory-item/:id" element={<AddNonInventoryItemPage />} />
                  <Route path="/non-inventory-list" element={<NonInventoryItemListPage />} />
                  <Route path="/add-purchase-order" element={<AddPurchaseOrderPage />} />
                  <Route path="/add-purchase-order/:id" element={<AddPurchaseOrderPage />} />
                  <Route path="/purchase-orders" element={<PurchaseOrderListPage />} />

                  <Route exact path="*" element={<ErrorPage />} />
                </Routes>
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
